/**
 * Mutations for user-created trackers and their items.
 *
 * Item updates (title / cell values) are optimistic against the
 * items list cache so cell edits land instantly, mirroring the briefs
 * board's custom-column UX.
 */
import {
  type ListTrackerRowsResponseDto,
  type SuggestDashboardRequestDto,
  type SuggestDashboardResponseDto,
  TrackersService,
  type TrackerItemResponseDto,
  type TrackerMemberDto,
  type TrackerResponseDto,
  type UpdateTrackerDto,
  type UpdateTrackerItemDto,
  type UpdateTrackerRowDto,
  type UpdateTrackerScopeDto,
} from '@inference.engine/workspace-api-client/web';
import {
  type InfiniteData,
  type QueryClient,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import { workspaceHttp } from '@/lib/api/http-client';
import { SUPPRESS_ERROR_NOTIFICATION_HEADER } from '@/lib/api/interceptors/problem-details.interceptor';
import { i18n } from '@/shared/i18n';

import { TRACKER_QUERY_KEYS } from './query-keys';

/** For every call whose caller reports its own failure, so the global toast stays silent. */
// eslint-disable-next-line i18next/no-literal-string -- HTTP header value
const QUIET = { headers: { [SUPPRESS_ERROR_NOTIFICATION_HEADER]: 'true' } };

/**
 * Automations run in a background worker AFTER a mutation's HTTP response
 * returns, so the optimistic reconcile shows the pre-automation state. A beat
 * later — twice, to also catch a chained rule — we re-read just the edited row
 * and patch it into the cache in place. We deliberately do NOT invalidate the
 * whole list: that forces a list-wide refetch and the board visibly blanks and
 * re-renders. Patching the single row means only its cells update, matching the
 * monday.com feel. Gated via `refetchForAutomations` so boards with no enabled
 * rules pay nothing.
 */
const AUTOMATION_REFETCH_DELAYS_MS = [700, 2000] as const;

function scheduleAutomationRefresh(
  refresh: () => void | Promise<void>,
  enabled: boolean | undefined,
): void {
  if (!enabled) return;
  for (const ms of AUTOMATION_REFETCH_DELAYS_MS) {
    setTimeout(() => void refresh(), ms);
  }
}

/**
 * Re-reads the given items and patches them into the board's items cache in
 * place, so only their cells update. There is no single-item endpoint, so this
 * lists the board once and takes the rows it needs. One the list no longer
 * returns has left the board (moved, archived or deleted) and is dropped.
 */
export async function refreshTrackerItems(
  queryClient: QueryClient,
  workspaceId: string,
  trackerId: string,
  itemIds: ReadonlySet<string>,
): Promise<void> {
  if (itemIds.size === 0) return;
  const res = await TrackersService.listItems({ client: workspaceHttp, path: { trackerId } });
  const fresh = new Map(
    (res.data?.items ?? []).filter((i) => itemIds.has(i.id)).map((i) => [i.id, i]),
  );
  queryClient.setQueryData<TrackerItemResponseDto[]>(
    TRACKER_QUERY_KEYS.items(workspaceId, trackerId),
    (prev) =>
      prev?.flatMap((item) => {
        if (!itemIds.has(item.id)) return [item];
        const next = fresh.get(item.id);
        return next ? [next] : [];
      }),
  );
}

/**
 * Runs the follow-up calls that finish a tracker the server has already created. If one
 * fails, the half-made tracker is deleted (its items cascade) before the error is rethrown,
 * so the caller's "could not create" is true and a retry does not leave a second copy.
 */
async function finishOrRollBack(
  trackerId: string,
  finish: () => Promise<TrackerResponseDto>,
): Promise<TrackerResponseDto> {
  try {
    return await finish();
  } catch (error) {
    await TrackersService.delete({ client: workspaceHttp, path: { trackerId }, ...QUIET }).catch(
      () => undefined,
    );
    throw error;
  }
}

/**
 * The hub reads the selected workspace's trackers from its own list, so the
 * organization list only needs refetching once the person is elsewhere.
 */
function markOrganizationListStale(queryClient: QueryClient) {
  return queryClient.invalidateQueries({
    queryKey: TRACKER_QUERY_KEYS.organization(),
    refetchType: 'none',
  });
}

/** A tracker the rollback could not remove still shows up straight away. */
function invalidateList(queryClient: QueryClient, workspaceId: string) {
  return queryClient.invalidateQueries({
    queryKey: TRACKER_QUERY_KEYS.listByWorkspace(workspaceId),
  });
}

/**
 * Create a tracker. A starting `layout` — a template snapshot, or the board a
 * record-backed type opens with — is applied in a second call, because create
 * itself takes only name, source and grouping.
 */
export function useCreateTrackerMutation({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      name: string;
      description?: string;
      itemSource?: TrackerResponseDto['itemSource'];
      groupBy?: string | null;
      layout?: TrackerResponseDto['layout'] | null;
      icon?: TrackerResponseDto['icon'];
      hue?: TrackerResponseDto['hue'];
    }) => {
      const createRes = await TrackersService.create({
        client: workspaceHttp,
        body: {
          name: input.name,
          ...(input.description ? { description: input.description } : {}),
          ...(input.itemSource ? { itemSource: input.itemSource } : {}),
          ...(input.groupBy ? { groupBy: input.groupBy } : {}),
          ...(input.icon ? { icon: input.icon } : {}),
          ...(input.hue ? { hue: input.hue } : {}),
        },
        ...QUIET,
      });
      const created = createRes.data;
      if (!created) throw new Error('tracker create returned an empty body');
      const layout = input.layout;
      if (!layout) return created;

      return finishOrRollBack(created.id, async () => {
        const layoutRes = await TrackersService.update({
          client: workspaceHttp,
          path: { trackerId: created.id },
          body: { layout },
          ...QUIET,
        });
        return layoutRes.data ?? created;
      });
    },
    onError: () => invalidateList(queryClient, workspaceId),
    onSuccess: (created) => {
      void markOrganizationListStale(queryClient);
      queryClient.setQueryData<TrackerResponseDto[]>(
        TRACKER_QUERY_KEYS.listByWorkspace(workspaceId),
        (prev) => (prev ? [...prev, created] : [created]),
      );
      queryClient.setQueryData(TRACKER_QUERY_KEYS.detail(workspaceId, created.id), created);
    },
  });
}

/**
 * Duplicate a tracker: create a copy carrying the source's item source,
 * grouping, and board layout (columns / widths / custom groups). Free-form
 * trackers own their items, so those are copied too (title + position + cell
 * values); briefs trackers read shared workspace briefs, so there's nothing
 * per-tracker to copy. Orchestrated client-side — no bulk endpoint exists.
 */
export function useDuplicateTrackerMutation({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (source: TrackerResponseDto) => {
      const createRes = await TrackersService.create({
        client: workspaceHttp,
        body: {
          name: i18n.t('trackers:trackers.trackerCopy', { name: source.name }),
          description: source.description ?? undefined,
          itemSource: source.itemSource,
          ...(source.groupBy ? { groupBy: source.groupBy } : {}),
          icon: source.icon,
          hue: source.hue,
        },
        ...QUIET,
      });
      const created = createRes.data;
      if (!created) throw new Error('tracker duplicate returned an empty body');

      return finishOrRollBack(created.id, async () => {
        let tracker = created;
        if (source.layout) {
          const layoutRes = await TrackersService.update({
            client: workspaceHttp,
            path: { trackerId: tracker.id },
            body: { layout: source.layout },
            ...QUIET,
          });
          if (layoutRes.data) tracker = layoutRes.data;
        }

        if (source.itemSource === 'text') {
          const itemsRes = await TrackersService.listItems({
            client: workspaceHttp,
            path: { trackerId: source.id },
            ...QUIET,
          });
          for (const item of itemsRes.data?.items ?? []) {
            const createdItem = await TrackersService.createItem({
              client: workspaceHttp,
              path: { trackerId: tracker.id },
              body: { title: item.title },
              ...QUIET,
            });
            if (!createdItem.data) continue;
            await TrackersService.updateItem({
              client: workspaceHttp,
              path: { trackerId: tracker.id, itemId: createdItem.data.id },
              body: {
                position: item.position,
                ...(item.customColumnValues ? { customColumnValues: item.customColumnValues } : {}),
              },
              ...QUIET,
            });
          }
        }

        return tracker;
      });
    },
    onError: () => invalidateList(queryClient, workspaceId),
    onSuccess: (created) => {
      void markOrganizationListStale(queryClient);
      queryClient.setQueryData<TrackerResponseDto[]>(
        TRACKER_QUERY_KEYS.listByWorkspace(workspaceId),
        (prev) => (prev ? [...prev, created] : [created]),
      );
      queryClient.setQueryData(TRACKER_QUERY_KEYS.detail(workspaceId, created.id), created);
    },
  });
}

export function useUpdateTrackerMutation({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trackerId, body }: { trackerId: string; body: UpdateTrackerDto }) => {
      const response = await TrackersService.update({
        client: workspaceHttp,
        path: { trackerId },
        body,
      });
      if (!response.data) throw new Error('tracker update returned an empty body');
      return response.data;
    },
    onSuccess: (updated) => {
      void markOrganizationListStale(queryClient);
      queryClient.setQueryData(TRACKER_QUERY_KEYS.detail(workspaceId, updated.id), updated);
      queryClient.setQueryData<TrackerResponseDto[]>(
        TRACKER_QUERY_KEYS.listByWorkspace(workspaceId),
        (prev) => prev?.map((t) => (t.id === updated.id ? updated : t)),
      );
    },
  });
}

export function useDeleteTrackerMutation({ workspaceId }: { workspaceId: string }) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ trackerId }: { trackerId: string }) => {
      await TrackersService.delete({ client: workspaceHttp, path: { trackerId }, ...QUIET });
    },
    onSuccess: (_data, { trackerId }) => {
      void markOrganizationListStale(queryClient);
      queryClient.setQueryData<TrackerResponseDto[]>(
        TRACKER_QUERY_KEYS.listByWorkspace(workspaceId),
        (prev) => prev?.filter((t) => t.id !== trackerId),
      );
      queryClient.removeQueries({ queryKey: TRACKER_QUERY_KEYS.detail(workspaceId, trackerId) });
      queryClient.removeQueries({ queryKey: TRACKER_QUERY_KEYS.items(workspaceId, trackerId) });
    },
  });
}

export function useCreateTrackerItemMutation({
  workspaceId,
  trackerId,
}: {
  workspaceId: string;
  trackerId: string;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: { title: string }) => {
      const response = await TrackersService.createItem({
        client: workspaceHttp,
        path: { trackerId },
        body,
      });
      if (!response.data) throw new Error('tracker item create returned an empty body');
      return response.data;
    },
    onSuccess: (created) => {
      queryClient.setQueryData<TrackerItemResponseDto[]>(
        TRACKER_QUERY_KEYS.items(workspaceId, trackerId),
        (prev) => (prev ? [...prev, created] : [created]),
      );
    },
  });
}

/**
 * Optimistic item update: patches the items cache immediately, rolls back on
 * error, reconciles with the server response on success.
 */
export function useUpdateTrackerItemMutation({
  workspaceId,
  trackerId,
  refetchForAutomations,
}: {
  workspaceId: string;
  trackerId: string;
  /** When true, re-fetch the items list shortly after a cell edit so async
   *  automation results (group moves, date/status writes) surface on the board
   *  without a manual refresh. Pass the board's "has enabled automations" flag. */
  refetchForAutomations?: boolean;
}) {
  const queryClient = useQueryClient();
  const itemsKey = TRACKER_QUERY_KEYS.items(workspaceId, trackerId);
  return useMutation({
    mutationFn: async ({
      itemId,
      body,
      reportsErrors,
    }: {
      itemId: string;
      body: UpdateTrackerItemDto;
      /** The caller names the cell and the reason itself; see `trackerRowWriteErrorCode`. */
      reportsErrors?: boolean;
    }) => {
      const response = await TrackersService.updateItem({
        client: workspaceHttp,
        path: { trackerId, itemId },
        body,
        ...(reportsErrors ? QUIET : {}),
      });
      if (!response.data) throw new Error('tracker item update returned an empty body');
      return response.data;
    },
    onMutate: async ({ itemId, body }) => {
      await queryClient.cancelQueries({ queryKey: itemsKey });
      const previous = queryClient.getQueryData<TrackerItemResponseDto[]>(itemsKey);
      queryClient.setQueryData<TrackerItemResponseDto[]>(itemsKey, (prev) =>
        prev?.map((item) =>
          item.id === itemId
            ? {
                ...item,
                ...(body.title !== undefined ? { title: body.title } : {}),
                ...(body.position !== undefined ? { position: body.position } : {}),
                ...(body.customColumnValues !== undefined
                  ? { customColumnValues: body.customColumnValues ?? undefined }
                  : {}),
                ...(body.customColumnValuesPatch !== undefined
                  ? {
                      customColumnValues: applyCellPatch(
                        item.customColumnValues,
                        body.customColumnValuesPatch,
                      ),
                    }
                  : {}),
              }
            : item,
        ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(itemsKey, context.previous);
    },
    onSuccess: (updated, { body }) => {
      queryClient.setQueryData<TrackerItemResponseDto[]>(itemsKey, (prev) =>
        prev?.map((item) => (item.id === updated.id ? updated : item)),
      );
      // A cell-value edit may have tripped an automation; re-read the edited row
      // and patch it in place. Position-only moves can't trigger rules — skip.
      if (body.customColumnValues !== undefined || body.customColumnValuesPatch !== undefined) {
        scheduleAutomationRefresh(
          () => refreshTrackerItems(queryClient, workspaceId, trackerId, new Set([updated.id])),
          refetchForAutomations,
        );
      }
    },
  });
}

/** The item's cells once the server has applied a per-key patch: a null clears the cell. */
function applyCellPatch(
  current: Record<string, unknown> | undefined,
  patch: Record<string, unknown>,
): Record<string, unknown> {
  const cleared = new Set(
    Object.entries(patch)
      .filter(([, value]) => value === null)
      .map(([key]) => key),
  );
  return Object.fromEntries(
    Object.entries({ ...current, ...patch }).filter(([key]) => !cleared.has(key)),
  );
}

type RowsCache = InfiniteData<ListTrackerRowsResponseDto, string | null>;

/**
 * Edit one row of a record-backed board.
 *
 * The cell is patched into every cached rows query for the board so the edit
 * lands instantly, but the counts are NOT touched: they are the server's
 * answer over the whole matching set, and guessing how an edit moves them is
 * exactly the local arithmetic a record-backed board exists to avoid. The
 * invalidation on settle is what makes them right again — and it is also what
 * drops a row that the edit has moved out of the current filter.
 */
export function useUpdateTrackerRowMutation({
  workspaceId,
  trackerId,
}: {
  workspaceId: string;
  trackerId: string;
}) {
  const queryClient = useQueryClient();
  const rowsKey = TRACKER_QUERY_KEYS.allRows(workspaceId, trackerId);

  return useMutation({
    mutationFn: async ({ rowKey, body }: { rowKey: string; body: UpdateTrackerRowDto }) => {
      const response = await TrackersService.updateRow({
        client: workspaceHttp,
        path: { trackerId, rowKey },
        body,
        // The board names the cell and the reason itself; see `trackerRowWriteErrorCode`.
        ...QUIET,
      });
      if (!response.data) throw new Error('tracker row update returned an empty body');
      return response.data;
    },
    onMutate: async ({ rowKey, body }) => {
      await queryClient.cancelQueries({ queryKey: rowsKey });
      const previous = queryClient.getQueriesData<RowsCache>({ queryKey: rowsKey });
      queryClient.setQueriesData<RowsCache>({ queryKey: rowsKey }, (cache) =>
        patchRow(cache, rowKey, (row) => ({
          ...row,
          fields: { ...row.fields, ...body.fields },
          customColumnValues: mergeCellValues(row.customColumnValues, body.customColumnValues),
        })),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      for (const [key, cache] of context?.previous ?? []) queryClient.setQueryData(key, cache);
    },
    onSuccess: (updated) => {
      queryClient.setQueriesData<RowsCache>({ queryKey: rowsKey }, (cache) =>
        patchRow(cache, updated.rowKey, () => updated),
      );
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: rowsKey });
    },
  });
}

/**
 * What the row's cells will read once the server has applied the write: the
 * request patches the keys it names, so the optimistic cache has to as well or
 * every cell the edit left out blanks until the refetch answers.
 */
function mergeCellValues(
  current: Record<string, unknown>,
  patch: UpdateTrackerRowDto['customColumnValues'],
): Record<string, unknown> {
  if (patch === undefined) return current;
  if (patch === null) return {};
  return { ...current, ...patch };
}

function patchRow(
  cache: RowsCache | undefined,
  rowKey: string,
  update: (
    row: ListTrackerRowsResponseDto['data'][number],
  ) => ListTrackerRowsResponseDto['data'][number],
): RowsCache | undefined {
  if (!cache) return cache;
  return {
    ...cache,
    pages: cache.pages.map((page) => ({
      ...page,
      data: page.data.map((row) => (row.rowKey === rowKey ? update(row) : row)),
    })),
  };
}

export function useUpdateTrackerMembersMutation({
  workspaceId,
  trackerId,
}: {
  workspaceId: string;
  trackerId: string;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (members: TrackerMemberDto[]) => {
      const response = await TrackersService.updateMembers({
        client: workspaceHttp,
        path: { trackerId },
        body: { members },
      });
      if (!response.data) throw new Error('tracker members update returned an empty body');
      return response.data;
    },
    onSuccess: (data) => {
      void markOrganizationListStale(queryClient);
      queryClient.setQueryData(TRACKER_QUERY_KEYS.members(workspaceId, trackerId), data);
      // Access may have changed (restricted/opened) — refresh the list.
      void queryClient.invalidateQueries({
        queryKey: TRACKER_QUERY_KEYS.listByWorkspace(workspaceId),
      });
    },
  });
}

export function useUpdateTrackerScopeMutation({
  workspaceId,
  trackerId,
}: {
  workspaceId: string;
  trackerId: string;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: UpdateTrackerScopeDto) => {
      const response = await TrackersService.updateScope({
        client: workspaceHttp,
        path: { trackerId },
        body,
      });
      if (!response.data) throw new Error('tracker scope update returned an empty body');
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.setQueryData(TRACKER_QUERY_KEYS.scope(workspaceId, trackerId), data);
      // The new scope changes the rows key, so the board fetches under it
      // anyway. Pages read under the old scope are only marked stale, so going
      // back to it refetches rather than serving rows from before the change.
      void queryClient.invalidateQueries({
        queryKey: TRACKER_QUERY_KEYS.allRows(workspaceId, trackerId),
        refetchType: 'none',
      });
      // Hub card counts follow the stored scope.
      void queryClient.invalidateQueries({
        queryKey: TRACKER_QUERY_KEYS.listByWorkspace(workspaceId),
        exact: true,
        refetchType: 'none',
      });
      void queryClient.invalidateQueries({
        queryKey: TRACKER_QUERY_KEYS.organization(),
        exact: true,
        refetchType: 'none',
      });
    },
  });
}

export function useDeleteTrackerItemMutation({
  workspaceId,
  trackerId,
}: {
  workspaceId: string;
  trackerId: string;
}) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ itemId }: { itemId: string }) => {
      await TrackersService.deleteItem({ client: workspaceHttp, path: { trackerId, itemId } });
    },
    onSuccess: (_data, { itemId }) => {
      queryClient.setQueryData<TrackerItemResponseDto[]>(
        TRACKER_QUERY_KEYS.items(workspaceId, trackerId),
        (prev) => prev?.filter((item) => item.id !== itemId),
      );
    },
  });
}

/**
 * Ask the backend's AI dashboard designer for widgets, given a compact board
 * profile (columns + distributions — built by the trackers-board widget). An
 * empty `widgets` array means the LLM was unavailable; callers fall back to
 * the deterministic starter dashboard.
 */
export function useSuggestDashboardMutation({ trackerId }: { trackerId: string }) {
  return useMutation<SuggestDashboardResponseDto, Error, SuggestDashboardRequestDto>({
    mutationFn: async (profile) => {
      const response = await TrackersService.suggestDashboard({
        client: workspaceHttp,
        path: { trackerId },
        body: profile,
      });
      return response.data ?? { widgets: [] };
    },
  });
}
